import {useState} from 'react';

import {ExpandMore, Sync as SyncIcon} from '@mui/icons-material';
import {IconButton, Grid, Tooltip} from '@mui/material';
import {alpha, styled} from '@mui/material/styles';
import PropTypes from 'prop-types';

import {BREAKPOINTS} from '../../../../../const';
import useBreakpoint from '../../../../../hooks/dom/useBreakpoint';
import useReportParameters from '../../../../../hooks/providers/useReportParameters';
import Button from '../../../../form/buttons/Button/Button';
import PartialUpdateSelectTablesPopper from '../PartialUpdateSelectTablesPopper/PartialUpdateSelectTablesPopper/PartialUpdateSelectTablesPopper';

const UpdateNowButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isUpdating'
})(({isUpdating, theme}) => ({
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
  marginRight: 1,
  '& .MuiSvgIcon-root': {
    transition: 'transform 0.5s linear',
    animation: isUpdating ? 'spin 1s linear infinite' : 'none'
  },
  '@keyframes spin': {
    '0%': {transform: 'rotate(0deg)'},
    '100%': {transform: 'rotate(360deg)'}
  }
}));

const UpdateArrowButton = styled(IconButton)(({theme}) => ({
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  backgroundColor: theme.palette.success.main,
  boxShadow: theme.shadows[2],
  transition: 'background-color 0.2s, box-shadow 0.2s',

  '&:hover': {
    backgroundColor: theme.palette.success.dark,
    boxShadow: theme.shadows[4]
  },

  '&.Mui-disabled': {
    backgroundColor: alpha(theme.palette.success.main, 0.55),
    color: alpha(theme.palette.success.main, 0.26),
    boxShadow: theme.shadows[0]
  }
}));

const UpdateButton = ({isUpdating, onUpdateNowButton}) => {
  const [isPartialUpdatePopperOpen, setIsPartialUpdatePopperOpen] = useState(false);
  const [partialUpdatePopperAnchorEl, setPartialUpdatePopperAnchorEl] = useState(null);
  const {areAllSourcesConnected} = useReportParameters();

  const handleUpdateArrowClick = e => {
    e.stopPropagation();
    setIsPartialUpdatePopperOpen(prev => !prev);
    setPartialUpdatePopperAnchorEl(e.currentTarget);
  };

  const isButtonDisabled = isUpdating || !areAllSourcesConnected;
  const currentBreakpoint = useBreakpoint();
  const isExtraSmallScreen = currentBreakpoint === BREAKPOINTS.xs;
  const updateButtonText = isExtraSmallScreen ? 'Actualiser' : 'Actualiser maintenant';

  return (
    <Grid item>
      <Grid container item wrap="nowrap">
        <UpdateNowButton
          variant="contained"
          color="success"
          isUpdating={isUpdating}
          startIcon={
            <SyncIcon
              sx={{
                fontSize: '14px !important'
              }}
            />
          }
          disabled={isButtonDisabled}
          onClick={onUpdateNowButton}
          size="small"
          sx={{
            fontSize: {
              xs: 10,
              sm: 12
            }
          }}
        >
          {updateButtonText}
        </UpdateNowButton>

        <UpdateArrowButton disabled={isButtonDisabled} onClick={handleUpdateArrowClick} size="small" aria-label="toggle refresh menu" aria-expanded={isPartialUpdatePopperOpen} edge="end">
          <ExpandMore
            sx={{
              color: 'white !important',
              fontSize: 19,
              transition: 'transform 0.25s ease',
              transform: isPartialUpdatePopperOpen ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          />
        </UpdateArrowButton>
      </Grid>

      <PartialUpdateSelectTablesPopper
        isOpen={isPartialUpdatePopperOpen}
        anchorEl={partialUpdatePopperAnchorEl}
        onClickAway={() => {
          setPartialUpdatePopperAnchorEl(null);
          setIsPartialUpdatePopperOpen(false);
        }}
      />
    </Grid>
  );
};

UpdateButton.propTypes = {
  isUpdating: PropTypes.bool.isRequired,
  onUpdateNowButton: PropTypes.func.isRequired
};

export default UpdateButton;
